import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import filter from 'ramda/src/filter';
import moment from 'moment';
import classNames from 'classnames';
import CheckIcon from '@components/@core/CheckIcon';
import EditDropdown from '@components/EditDropdown';
import Icon from '@components/@core/Icon';
import Thumbnail from '@components/@core/Thumbnail';
import { UserRoles, AdminDotsType } from '~/services/config';
import ChildFeed from './ChildFeed';

const LearnFeedGrid = ({ 
    assignedBy, 
    card = { data: {} }, 
    dataCy, 
    dueDate = '3/26/2018', 
    modules = [], 
    programId, 
    onChange, 
    onClickChild = () => {}
}) => {
    const userId = useSelector((state) => state.app.id);
    const role = useSelector((state) => state.app.app_role_id);

    const [direction, setDirection] = useState('bottom');
    const [scrollable, setScrollable] = useState(false);

    // Helper function to safely format dates
    const formatDateForMobile = (dateString) => {
        // Check if date string looks like a valid format
        if (!dateString || typeof dateString !== 'string') return dateString;
        
        // Avoid problematic formats like "Apr 25, 25" 
        if (dateString.match(/^[A-Za-z]{3}\s+\d{1,2},\s+\d{1,2}$/)) {
            return dateString; // Return as-is for invalid format
        }
        
        // Try to create moment only if it looks like a valid date
        const momentDate = moment(dateString, ['M/D/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'MMM DD, YYYY'], true);
        return momentDate.isValid() ? momentDate.format('MMM DD') : dateString;
    };

    const handleScroll = (event) => {
        if (!card.data || card.data.child_count < 5) return;
        const bottom = (card.data.child_count - 4) * 85;
        if (event.currentTarget.scrollTop >= bottom) {
            setDirection('top');
        } else if (event.currentTarget.scrollTop === 0) {
            setDirection('bottom');
        }
    };

    const { data = {} } = card;
    const overlay = card.retention_quiz && (
        <div className="c-lfg-overlay">
            <div className="quiz-mark">
                <Icon name="fas fa-circle-question" color="#676767" />
                <span className="dsl-m12 mt-3">Retention Quiz</span>
            </div>
        </div>
    );

    let dotsMenu = filter((x) => x !== 'Assign' && x !== 'Bulk Unassign', AdminDotsType);
    if (role >= UserRoles.USER && assignedBy !== userId) {
        dotsMenu = filter((x) => x !== 'Edit', dotsMenu);
    }

    return (
        <div className="c-lfg d-flex ssm-column" data-cy={dataCy}>
            <div className="d-flex-7 me-2 ssm-r-0">
                <div className="d-flex mb-3">
                    <CheckIcon className="mt-1" size={26} checked={(data.child_count || 0) === (data.completed || 0)} onClick={() => onClickChild(0, 'course')} />
                    <div className="d-flex d-flex-1">
                        <div className="ml-2 w-80">
                            <p className="dsl-m12 text-400 mb-1">{!programId ? (assignedBy === userId ? 'Self Assigned' : 'Health Coach Assigned') : 'Health Plan Assigned : '}</p>
                            <p className={classNames('dsl-b16 text-400 mb-0 truncate-one word-break', (data.child_count || 0) === (data.completed || 0) && 'text-line-through')}>{data.name || ''}</p>
                        </div>
                        <div className="ml-auto w-20">
                            <p className="dsl-m12 text-400 text-end text-nowrap mb-1">Due Date</p>
                            <p className="dsl-m16 text-400 text-end mb-0">
                                <span className="desktop-block">{dueDate}</span>
                                <span className="mobile-screen">{formatDateForMobile(dueDate)}</span>
                            </p>
                        </div>
                    </div>
                </div>
                <div>
                    <Thumbnail className="cursor-pointer" source={data.thumb_url || ''} size="responsive" overlay={overlay} onClick={() => onClickChild(0, 'course')} />
                    <div className="pt-4 c-lfg-desc desktop-block">
                        <p className="dsl-b16">{data.description || ''}</p>
                    </div>
                </div>
            </div>
            <div className="d-flex-5 ms-2 ssm-l-0">
                <div className="d-flex mb-3 ssm-b-0 ssm-t-3">
                    <div className="d-flex-2 ssm-column-reverse">
                        <p className="dsl-m12 text-400 text-center mb-1">Modules</p>
                        <p className="dsl-b16 text-center text-400 mb-0">{data.child_count || 0}</p>
                    </div>
                    <div className="d-flex-2 ssm-column-reverse">
                        <p className="dsl-m12 text-400 text-center mb-1">Completed</p>
                        <p className="dsl-b16 text-center text-400 mb-0">{data.completed || 0}</p>
                    </div>
                    <div className="d-flex-2 ssm-column-reverse">
                        <p className="dsl-m12 text-400 text-center mb-1">Remaining</p>
                        <p className="dsl-b16 text-center text-400 mb-0">{(data.child_count || 0) - (data.completed || 0) || 0}</p>
                    </div>
                    <div className="d-flex-1 tablet-screen">{card.card_template_id && <EditDropdown className="ml-auo" options={dotsMenu} onChange={(e) => onChange && onChange(e, card)} />}</div>
                </div>

                <div className={classNames('desktop-block', scrollable && 'mcontent')} onScroll={handleScroll}>
                    {modules.map(
                        (child, index) =>
                            (scrollable || (index < 4 && !scrollable)) && (
                                <ChildFeed
                                    key={child.id}
                                    type={child.card_type_id}
                                    name={child.data.name || child.name}
                                    status={child.status}
                                    disabled={card.retention_quiz && child.card_type_id !== 16}
                                    onClick={() => onClickChild(index, 'module')}
                                />
                            )
                    )}
                </div>
                {modules.length > 4 && (
                    <div className="d-h-center mt-4" onClick={() => setScrollable(!scrollable)}>
                        <span className="dsl-p14">{scrollable ? 'LESS' : 'MORE'} MODULES&nbsp;</span>
                        <Icon name={scrollable ? 'fas fa-angle-up' : 'fas fa-angle-down'} size={14} color="#376caf" />
                    </div>
                )}
                {card.child_count > 4 && (
                    <div className="d-h-center mt-4 desktop-block">
                        <span className="dsl-p12 bold">More Modules</span>
                        <Icon name={`fas fa-angle-${direction === 'top' ? 'up' : 'down'}`} className="ms-2 bold" size={12} color="#376caf" />
                    </div>
                )}
            </div>
        </div>
    );
};

LearnFeedGrid.propTypes = {
    assignedBy: PropTypes.number,
    card: PropTypes.any,
    dataCy: PropTypes.string,
    dueDate: PropTypes.string,
    modules: PropTypes.any,
    programId: PropTypes.number,
    onChange: PropTypes.func,
    onClickChild: PropTypes.func,
};

export default LearnFeedGrid;
